import { useState, useEffect } from 'react';
import { X, Loader2, Palette } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';

const COLOR_PRESETS = [
  '#6B7280',
  '#EF4444',
  '#F59E0B',
  '#10B981',
  '#3B82F6',
  '#8B5CF6',
  '#EC4899',
  '#F97316',
  '#14B8A6',
  '#6366F1',
  '#84CC16',
  '#0EA5E9',
];

interface StatusModalProps {
  isOpen: boolean;
  status?: { id: string; name: string; color: string; isDefault: boolean; isClosed: boolean };
  onClose: () => void;
  onSubmit: (data: { name: string; color: string; isDefault?: boolean; isClosed?: boolean }) => void;
  isLoading?: boolean;
}

export function StatusModal({ isOpen, status, onClose, onSubmit, isLoading }: StatusModalProps) {
  const [name, setName] = useState('');
  const [color, setColor] = useState(COLOR_PRESETS[0]);
  const [isDefault, setIsDefault] = useState(false);
  const [isClosed, setIsClosed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (status) {
      setName(status.name);
      setColor(status.color);
      setIsDefault(status.isDefault);
      setIsClosed(status.isClosed);
    } else {
      setName('');
      setColor(COLOR_PRESETS[0]);
      setIsDefault(false);
      setIsClosed(false);
    }
    setError(null);
  }, [status, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Status name is required');
      return;
    }
    setError(null);
    onSubmit({ name: name.trim(), color, isDefault, isClosed });
  };

  const handleClose = () => {
    setName('');
    setColor(COLOR_PRESETS[0]);
    setIsDefault(false);
    setIsClosed(false);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-secondary/60 backdrop-blur-sm animate-fade-in"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-elevated animate-scale-in overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-grey-100">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-accent/10">
              <Palette className="w-5 h-5 text-accent" />
            </div>
            <h2 className="text-heading-4 text-secondary">
              {status ? 'Edit Status' : 'New Status'}
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-grey-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-grey-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-6 space-y-4">
            {/* Status Name */}
            <div>
              <label htmlFor="statusName" className="block text-sm font-medium text-secondary mb-2">
                Status Name <span className="text-error">*</span>
              </label>
              <input
                id="statusName"
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setError(null);
                }}
                placeholder="e.g., To Do, In Progress, Done"
                className="input"
                required
                autoFocus
              />
            </div>

            {/* Color Picker */}
            <div>
              <label className="block text-sm font-medium text-secondary mb-2">
                Color
              </label>
              <div className="grid grid-cols-6 gap-2">
                {COLOR_PRESETS.map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => setColor(preset)}
                    className={cn(
                      'w-8 h-8 rounded-lg transition-transform',
                      color === preset ? 'ring-2 ring-offset-2 ring-secondary scale-110' : ''
                    )}
                    style={{ backgroundColor: preset }}
                    title={preset}
                  />
                ))}
              </div>
            </div>

            {/* Checkboxes */}
            <div className="space-y-3 pt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                  className="w-4 h-4 rounded border-grey-300"
                />
                <span className="text-sm text-secondary">Set as default status</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isClosed}
                  onChange={(e) => setIsClosed(e.target.checked)}
                  className="w-4 h-4 rounded border-grey-300"
                />
                <span className="text-sm text-secondary">Treat as closed/done</span>
              </label>
            </div>

            {error && (
              <p className="text-sm text-error">{error}</p>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 bg-grey-50 border-t border-grey-100">
            <Button type="button" variant="ghost" onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!name.trim() || isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {status ? 'Saving...' : 'Creating...'}
                </>
              ) : (
                status ? 'Save Status' : 'Create Status'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
